import { useEffect, useRef } from 'react'
import * as faceapi from '@vladmandic/face-api/dist/face-api.esm-nobundle.js'
import {
    getGestureRecognizer,
    getPoseLandmarker,
    nextVideoTimestamp,
} from '../../WebAi/vision/mediapipeRuntime'

const MODEL_URL = process.env.PUBLIC_URL + '/models'

// MediaPipe Pose skeleton edges (33-landmark BlazePose order).
const POSE_EDGES = [
    [11, 12], [11, 13], [13, 15], [12, 14], [14, 16],
    [11, 23], [12, 24], [23, 24], [23, 25], [25, 27],
    [27, 29], [29, 31], [27, 31], [24, 26], [26, 28],
    [28, 30], [30, 32], [28, 32], [15, 17], [16, 18],
    [15, 19], [16, 20], [15, 21], [16, 22], [0, 11], [0, 12],
]

// Hand skeleton edges (MediaPipe 21-landmark order).
const HAND_EDGES = [
    [0, 1], [1, 2], [2, 3], [3, 4], [0, 5], [5, 6], [6, 7], [7, 8],
    [5, 9], [9, 10], [10, 11], [11, 12], [9, 13], [13, 14], [14, 15], [15, 16],
    [13, 17], [17, 18], [18, 19], [19, 20], [0, 17],
]

let faceLoad = null

// Live marker overlay drawn on top of the capture video so the user sees what
// the model "sees" while collecting samples: a face box for image (CNN)
// training, a body skeleton for pose training. Purely a UX aid — it does not
// affect the captured sample.
export default function CaptureMarkers({ videoRef, kind, active }) {
    const canvasRef = useRef(null)
    const rafRef = useRef(null)
    const busyRef = useRef(false)

    useEffect(() => {
        if (!active) return
        let stopped = false

        const ensureModels = async () => {
            if (kind === 'pose') {
                await getPoseLandmarker()
            } else if (kind === 'gesture') {
                await getGestureRecognizer()
            } else if (kind === 'face') {
                if (!faceapi.nets.tinyFaceDetector.isLoaded) {
                    if (!faceLoad)
                        faceLoad = faceapi.nets.tinyFaceDetector.loadFromUri(
                            MODEL_URL
                        )
                    await faceLoad
                }
            }
            // 'object' has no landmark marker — the whole frame is the sample.
        }

        const draw = async () => {
            if (stopped) return
            rafRef.current = requestAnimationFrame(draw)
            const video = videoRef.current
            const canvas = canvasRef.current
            if (!video || !canvas || video.readyState < 2) return
            const w = video.videoWidth
            const h = video.videoHeight
            if (!w || !h) return
            if (canvas.width !== w) canvas.width = w
            if (canvas.height !== h) canvas.height = h
            const ctx = canvas.getContext('2d')

            if (busyRef.current) return
            busyRef.current = true
            try {
                ctx.clearRect(0, 0, w, h)
                if (kind === 'pose') {
                    const landmarker = await getPoseLandmarker()
                    const res = landmarker.detectForVideo(
                        video,
                        nextVideoTimestamp(performance.now())
                    )
                    const pts = res && res.landmarks && res.landmarks[0]
                    if (pts) drawSkeleton(ctx, pts, w, h)
                } else if (kind === 'gesture') {
                    const rec = await getGestureRecognizer()
                    const res = rec.recognizeForVideo(
                        video,
                        nextVideoTimestamp(performance.now())
                    )
                    ;(res.landmarks || []).forEach((lm) =>
                        drawHand(ctx, lm, w, h)
                    )
                } else if (
                    kind === 'face' &&
                    faceapi.nets.tinyFaceDetector.isLoaded
                ) {
                    const dets = await faceapi.detectAllFaces(
                        video,
                        new faceapi.TinyFaceDetectorOptions({
                            inputSize: 224,
                            scoreThreshold: 0.4,
                        })
                    )
                    ctx.strokeStyle = '#4cc9a0'
                    ctx.lineWidth = 3
                    dets.forEach((d) => {
                        const b = d.box || d.detection?.box || {}
                        ctx.strokeRect(b.x, b.y, b.width, b.height)
                    })
                }
            } catch (e) {
                /* ignore frame errors */
            } finally {
                busyRef.current = false
            }
        }

        ensureModels().then(() => {
            if (!stopped) rafRef.current = requestAnimationFrame(draw)
        })

        return () => {
            stopped = true
            if (rafRef.current) cancelAnimationFrame(rafRef.current)
            const canvas = canvasRef.current
            if (canvas) {
                const ctx = canvas.getContext('2d')
                ctx && ctx.clearRect(0, 0, canvas.width, canvas.height)
            }
        }
    }, [active, kind, videoRef])

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                borderRadius: '10px',
            }}
        />
    )
}

// MediaPipe hand landmarks are already normalized 0-1 in image space.
function drawHand(ctx, lm, w, h) {
    ctx.strokeStyle = '#14b8a6'
    ctx.lineWidth = 3
    HAND_EDGES.forEach(([a, b]) => {
        ctx.beginPath()
        ctx.moveTo(lm[a].x * w, lm[a].y * h)
        ctx.lineTo(lm[b].x * w, lm[b].y * h)
        ctx.stroke()
    })
    ctx.fillStyle = '#f4813f'
    lm.forEach((p) => {
        ctx.beginPath()
        ctx.arc(p.x * w, p.y * h, 3.5, 0, Math.PI * 2)
        ctx.fill()
    })
}

// MediaPipe pose landmarks are normalized 0-1 {x, y, visibility}.
function drawSkeleton(ctx, pts, w, h) {
    const CONF = 0.4
    const vis = (p) => (p.visibility == null ? 1 : p.visibility)
    ctx.strokeStyle = '#f4813f'
    ctx.lineWidth = 3
    POSE_EDGES.forEach(([a, b]) => {
        const p1 = pts[a]
        const p2 = pts[b]
        if (!p1 || !p2 || vis(p1) < CONF || vis(p2) < CONF) return
        ctx.beginPath()
        ctx.moveTo(p1.x * w, p1.y * h)
        ctx.lineTo(p2.x * w, p2.y * h)
        ctx.stroke()
    })
    ctx.fillStyle = '#4a9fe0'
    pts.forEach((p) => {
        if (vis(p) < CONF) return
        ctx.beginPath()
        ctx.arc(p.x * w, p.y * h, 4, 0, Math.PI * 2)
        ctx.fill()
    })
}
